import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Phone } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { etichettaDifficolta, varianteDifficolta } from '../../lib/format'
import pagina from '../pagine.module.css'

interface LeadTeam {
  id: string
  interno: number | null
  nome: string
  ruolo: string
  settore: string
  personalita: string
  obiezioni: string[]
  difficolta: number
  descrizioneEstesa: string | null
}

export function ManagerLeads() {
  const queryClient = useQueryClient()
  const [modaleAperto, setModaleAperto] = useState(false)

  const q = useQuery({
    queryKey: ['manager', 'leads'],
    queryFn: async () => (await api.get<{ dati: LeadTeam[] }>('/manager/lead-profiles')).data.dati,
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Lead del team</h1>
          <div className={pagina.sottotitolo}>
            Lead condivisi: tutti gli SDR del team possono allenarsi con questi.
          </div>
        </div>
        <Button iconaSinistra={<Plus size={16} />} onClick={() => setModaleAperto(true)}>
          Crea lead
        </Button>
      </div>

      {q.data.length === 0 ? (
        <EmptyState
          icona={<Phone size={40} />}
          titolo="Nessun lead nel team"
          descrizione="Crea il primo lead che tutti gli SDR del team potranno usare."
          azione={<Button onClick={() => setModaleAperto(true)}>Crea il primo lead</Button>}
        />
      ) : (
        <div className={pagina.griglia}>
          {q.data.map((l) => (
            <Card key={l.id}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'var(--accent-soft)', color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Phone size={20} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{l.nome}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{l.ruolo}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Badge variante={varianteDifficolta(l.difficolta)}>
                  {etichettaDifficolta(l.difficolta)}
                </Badge>
                <span className={pagina.tag}>{l.settore}</span>
                {l.interno != null && <span className={pagina.tag}>Int. {l.interno}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <ModaleCreaLead
        aperto={modaleAperto}
        onChiudi={() => setModaleAperto(false)}
        onCreato={() => {
          setModaleAperto(false)
          queryClient.invalidateQueries({ queryKey: ['manager', 'leads'] })
        }}
      />
    </div>
  )
}

function ModaleCreaLead({ aperto, onChiudi, onCreato }:
  { aperto: boolean; onChiudi: () => void; onCreato: () => void }) {
  const [nome, setNome] = useState('')
  const [ruolo, setRuolo] = useState('')
  const [settore, setSettore] = useState('')
  const [personalita, setPersonalita] = useState('')
  const [obiezioniTesto, setObiezioniTesto] = useState('')
  const [difficolta, setDifficolta] = useState(2)
  const [obiettivoChiamata, setObiettivoChiamata] = useState('')
  // Obiettivi possibili gestiti come lista di tag con input per aggiungerne
  const [obiettiviPossibili, setObiettiviPossibili] = useState<string[]>([])
  const [nuovoObiettivo, setNuovoObiettivo] = useState('')
  const [voiceId, setVoiceId] = useState('')
  const [testingVoce, setTestingVoce] = useState(false)
  const [erroreVoce, setErroreVoce] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  const aggiungiObiettivo = () => {
    const v = nuovoObiettivo.trim()
    if (v && !obiettiviPossibili.includes(v)) {
      setObiettiviPossibili([...obiettiviPossibili, v])
      setNuovoObiettivo('')
    }
  }

  const testaVoce = async () => {
    if (!voiceId.trim()) return
    setErroreVoce(null)
    setTestingVoce(true)
    try {
      const res = await api.post<{ dati: { audio_base64: string } }>('/leads/test-voce', {
        voice_id: voiceId.trim(),
        testo: 'Ciao, come posso aiutarti? Sono qui per ascoltarti.',
      })
      const b64 = res.data.dati?.audio_base64
      if (!b64) throw new Error('Audio vuoto')
      const audio = new Audio(`data:audio/mpeg;base64,${b64}`)
      await audio.play()
    } catch (err) {
      setErroreVoce(messaggioErrore(err))
    } finally {
      setTestingVoce(false)
    }
  }

  const reset = () => {
    setNome(''); setRuolo(''); setSettore(''); setPersonalita('')
    setObiezioniTesto(''); setDifficolta(2)
    setObiettivoChiamata(''); setObiettiviPossibili([]); setNuovoObiettivo('')
    setVoiceId(''); setErroreVoce(null)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErrore(null)
    setCaricamento(true)
    try {
      await api.post('/manager/lead-profiles', {
        nome, ruolo, settore, personalita,
        obiezioni: obiezioniTesto.split('\n').map((s) => s.trim()).filter(Boolean),
        difficolta,
        ...(obiettivoChiamata.trim() ? { obiettivo_chiamata: obiettivoChiamata.trim() } : {}),
        ...(obiettiviPossibili.length > 0 ? { obiettivi_possibili: obiettiviPossibili } : {}),
        ...(voiceId.trim() ? { voice_id: voiceId.trim() } : {}),
      })
      reset()
      onCreato()
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  return (
    <Modal aperto={aperto} titolo="Nuovo lead team" onChiudi={onChiudi} larghezzaMax={560}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Campo label="Nome" valore={nome} onChange={setNome} />
        <Campo label="Ruolo" valore={ruolo} onChange={setRuolo} placeholder="es. Direttore acquisti" />
        <Campo label="Settore" valore={settore} onChange={setSettore} placeholder="es. Manifattura" />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Personalità</label>
          <textarea
            value={personalita}
            onChange={(e) => setPersonalita(e.target.value)}
            rows={3}
            className={pagina.input}
            style={{ height: 'auto', padding: 10, fontFamily: 'var(--font-sans)' }}
            required
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Obiezioni tipiche (una per riga)
          </label>
          <textarea
            value={obiezioniTesto}
            onChange={(e) => setObiezioniTesto(e.target.value)}
            rows={3}
            placeholder={"Non ho tempo\nMi mandi una mail\nQuanto costa?"}
            className={pagina.input}
            style={{ height: 'auto', padding: 10, fontFamily: 'var(--font-sans)' }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Difficoltà</label>
          <select
            value={difficolta}
            onChange={(e) => setDifficolta(parseInt(e.target.value, 10))}
            className={pagina.select}
            style={{ height: 40 }}
          >
            <option value={1}>Facile</option>
            <option value={2}>Medio</option>
            <option value={3}>Difficile</option>
          </select>
        </div>

        <Campo
          label="Obiettivo della chiamata (default)"
          valore={obiettivoChiamata}
          onChange={setObiettivoChiamata}
          placeholder="es. Fissare un appuntamento"
        />

        {/* Obiettivi possibili come lista di tag */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Obiettivi selezionabili dall'SDR
          </label>
          {obiettiviPossibili.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
              {obiettiviPossibili.map((o) => (
                <span key={o} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', background: 'var(--accent-soft)',
                  color: 'var(--accent)', borderRadius: 999, fontSize: 13,
                }}>
                  {o}
                  <button
                    type="button"
                    onClick={() => setObiettiviPossibili(obiettiviPossibili.filter((x) => x !== o))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, fontSize: 16, lineHeight: 1 }}
                    aria-label="Rimuovi"
                  >×</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={nuovoObiettivo}
              onChange={(e) => setNuovoObiettivo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); aggiungiObiettivo() }
              }}
              placeholder="Aggiungi un obiettivo e premi Invio"
              className={pagina.input}
              style={{ flex: 1, height: 40 }}
            />
            <Button type="button" variante="secondary" onClick={aggiungiObiettivo}>+</Button>
          </div>
        </div>

        {/* Voce ElevenLabs custom */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Voce ElevenLabs
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder="Voice ID (lascia vuoto per usare la default)"
              className={pagina.input}
              style={{ flex: 1, height: 40, fontFamily: 'var(--font-mono)' }}
            />
            <Button
              type="button"
              variante="secondary"
              disabled={!voiceId.trim() || testingVoce}
              caricamento={testingVoce}
              onClick={testaVoce}
            >
              Testa
            </Button>
          </div>
          {erroreVoce && (
            <div style={{ fontSize: 12, color: 'var(--danger)' }}>{erroreVoce}</div>
          )}
        </div>

        {errore && (
          <div style={{ padding: 10, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {errore}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variante="ghost" onClick={onChiudi}>Annulla</Button>
          <Button type="submit" caricamento={caricamento}>Crea lead</Button>
        </div>
      </form>
    </Modal>
  )
}

function Campo({ label, valore, onChange, placeholder }:
  { label: string; valore: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</label>
      <input
        type="text"
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={pagina.input}
        style={{ height: 40 }}
        required
      />
    </div>
  )
}
